import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Printer,
  ArrowUp,
  ArrowDown,
  Minus,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Quote,
  ClipboardList,
  TrendingUp,
  Users as UsersIcon,
  ExternalLink,
} from "lucide-react";
import { fetchRecap, fetchSalesScorecard } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

// -- Date helpers (Mon–Sun) ---------------------------------------------------
function toMonday(date) {
  const d = new Date(date);
  const day = d.getDay();              // 0=Sun, 1=Mon, ...
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}
function isoDate(date) {
  return date.toISOString().slice(0, 10);
}
function fmt(d) {
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

// -- Role display + metric label maps ----------------------------------------
const ROLE_LABEL = {
  admin: "Admin",
  dispatcher: "Dispatcher",
  sales: "Sales",
  lead_host: "Lead Host",
  host: "Host",
  driver: "Driver",
};
const ROLE_COLOR = {
  admin: "bg-amber-500/15 border-amber-500/30 text-amber-300",
  dispatcher: "bg-sky-500/15 border-sky-500/30 text-sky-300",
  sales: "bg-emerald-500/15 border-emerald-500/30 text-emerald-300",
  lead_host: "bg-violet-500/15 border-violet-500/30 text-violet-300",
  host: "bg-fuchsia-500/15 border-fuchsia-500/30 text-fuchsia-300",
  driver: "bg-orange-500/15 border-orange-500/30 text-orange-300",
};
const METRIC_LABEL = {
  interactions_logged: "Interactions",
  stage_advances: "Stage advances",
  contacts_enriched: "Contacts enriched",
  bus_tastings_attended: "Bus events",
  tastings_attended: "Bus events attended",
  unique_dispensaries: "Unique shops",
  feedback_mentions: "Mentions",
  tastings_scheduled: "Bus events scheduled",
  tastings_completed: "Bus events completed",
  routes_planned: "Routes planned",
  tasks_delegated: "Tasks delegated",
};

// Which metric keys contribute to the headline "total this week" for each
// role. Excludes derived/secondary keys (e.g. unique_dispensaries which
// would double-count tastings_attended).
const HEADLINE_KEYS = {
  sales: ["interactions_logged", "stage_advances", "contacts_enriched", "bus_tastings_attended"],
  dispatcher: ["tastings_scheduled", "tastings_completed", "routes_planned"],
  lead_host: ["tastings_attended", "feedback_mentions"],
  host: ["tastings_attended", "feedback_mentions"],
  driver: ["tastings_attended"],
  admin: ["tasks_delegated", "tastings_completed"],
};

// -- WoW delta chip -----------------------------------------------------------
function DeltaChip({ value }) {
  if (value === 0 || value === undefined || value === null) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-mono text-zinc-500">
        <Minus className="w-3 h-3" />
        0
      </span>
    );
  }
  const positive = value > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-mono ${
        positive ? "text-emerald-400" : "text-rose-400"
      }`}
    >
      {positive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      {Math.abs(value)}
    </span>
  );
}

// -- Per-person printable sheet ----------------------------------------------
function PersonSheet({ person, weekStart, weekEnd }) {
  const metrics = person.metrics || {};
  const wow = person.wow_deltas || {};
  const orderedKeys = Object.keys(metrics);
  // Pre-compute what each section would show in PRINT so we can hide
  // empty blocks instead of forcing filler onto paper.
  const printableMetricKeys = orderedKeys.filter(
    (k) => (metrics[k] || 0) > 0 || (wow[k] && wow[k] !== 0)
  );
  const taskCount =
    (person.admin_assigned_tasks?.length || 0) + (person.open_tasks?.length || 0);
  const feedbackCount = person.feedback_mentions?.length || 0;

  return (
    <section
      data-testid={`recap-sheet-${person.user_id}`}
      data-role={person.role}
      className="recap-sheet panel p-6 flex flex-col gap-5"
    >
      {/* Header */}
      <header className="flex items-start justify-between gap-4 border-b border-zinc-800 pb-4">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2.5">
            <h2 className="text-xl font-semibold tracking-tight text-zinc-50">
              {person.name}
            </h2>
            <span
              className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded border ${ROLE_COLOR[person.role] || "bg-zinc-800 border-zinc-700 text-zinc-300"}`}
            >
              {ROLE_LABEL[person.role] || person.role}
            </span>
          </div>
          <p className="text-xs text-zinc-500 font-mono">
            {person.email} · {fmt(weekStart)} → {fmt(weekEnd)}
          </p>
        </div>
          <Button
          variant="outline"
          size="sm"
          data-testid={`recap-print-${person.user_id}`}
          onClick={() => {
            // Hide every OTHER sheet, then print just this one.
            const targetTestId = `recap-sheet-${person.user_id}`;
            const all = document.querySelectorAll(".recap-sheet");
            all.forEach((el) => {
              if (el.getAttribute("data-testid") !== targetTestId) {
                el.classList.add("hide-on-print");
              }
            });
            document.body.classList.add("recap-printing");
            window.print();
            setTimeout(() => {
              all.forEach((el) => el.classList.remove("hide-on-print"));
              document.body.classList.remove("recap-printing");
            }, 500);
          }}
          className="no-print bg-zinc-900 border-zinc-700 hover:bg-zinc-800 hover:text-zinc-50"
        >
          <Printer className="w-3.5 h-3.5 mr-1.5" />
          Print
        </Button>
      </header>

      {/* Section 1: Tasks for upcoming week (admin-delegated first) */}
      <div className={`flex flex-col gap-3 ${taskCount === 0 ? "print-hidden" : ""}`}>
        <h3 className="text-[11px] uppercase tracking-[0.14em] font-mono text-zinc-500 flex items-center gap-1.5">
          <ClipboardList className="w-3.5 h-3.5" />
          Tasks heading into next week
        </h3>
        {person.admin_assigned_tasks?.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <div className="text-[10px] uppercase tracking-wider text-amber-400 font-mono">
              Assigned by admin
            </div>
            <ul className="flex flex-col gap-1.5">
              {person.admin_assigned_tasks.map((t) => (
                <li
                  key={t.id}
                  data-testid={`recap-admin-task-${t.id}`}
                  className="text-sm text-zinc-200 bg-amber-500/5 border border-amber-500/20 rounded px-3 py-2 flex items-start gap-2"
                >
                  <AlertCircle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{t.title}</div>
                    <div className="text-xs text-zinc-500">
                      {t.dispensary_name}
                      {t.due_date ? ` · Due ${fmt(t.due_date)}` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        {(() => {
          const otherOpen = (person.open_tasks || []).filter(
            (t) => !person.admin_assigned_tasks?.some((at) => at.id === t.id)
          );
          if (otherOpen.length === 0 && person.admin_assigned_tasks?.length === 0) {
            return (
              <p className="text-xs italic text-zinc-600">No open tasks.</p>
            );
          }
          if (otherOpen.length === 0) return null;
          return (
            <div className="flex flex-col gap-1.5">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                Other open tasks
              </div>
              <ul className="flex flex-col gap-1.5">
                {otherOpen.slice(0, 12).map((t) => (
                  <li
                    key={t.id}
                    data-testid={`recap-task-${t.id}`}
                    className="text-sm text-zinc-300 bg-zinc-900/60 border border-zinc-800 rounded px-3 py-2 flex items-start gap-2"
                  >
                    <span className="w-3.5 h-3.5 rounded-sm border border-zinc-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div>{t.title}</div>
                      <div className="text-xs text-zinc-500">
                        {t.dispensary_name}
                        {t.due_date ? ` · Due ${fmt(t.due_date)}` : ""}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}
      </div>

      {/* Section 2: Last week's metrics */}
      <div className={`flex flex-col gap-2 ${printableMetricKeys.length === 0 ? "print-hidden" : ""}`}>
        <h3 className="text-[11px] uppercase tracking-[0.14em] font-mono text-zinc-500">
          Last week's numbers
        </h3>
        {orderedKeys.length === 0 ? (
          <p className="text-xs italic text-zinc-600">
            No tracked metrics for this role yet.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {orderedKeys.map((k) => {
              const isZeroQuiet = (metrics[k] || 0) === 0 && (!wow[k] || wow[k] === 0);
              return (
              <div
                key={k}
                data-testid={`recap-metric-${person.user_id}-${k}`}
                data-print-hide-zero={isZeroQuiet ? "" : undefined}
                className="bg-zinc-900/60 border border-zinc-800 rounded px-3 py-2.5"
              >
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                  {METRIC_LABEL[k] || k}
                </div>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <span className="text-2xl font-semibold tracking-tight text-zinc-50">
                    {metrics[k]}
                  </span>
                  <DeltaChip value={wow[k]} />
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Section 3: Feedback mentions */}
      <div className={`flex flex-col gap-2 ${feedbackCount === 0 ? "print-hidden" : ""}`}>
        <h3 className="text-[11px] uppercase tracking-[0.14em] font-mono text-zinc-500 flex items-center gap-1.5">
          <Quote className="w-3.5 h-3.5" />
          Anonymous feedback mentions
        </h3>
        {(person.feedback_mentions || []).length === 0 ? (
          <p className="text-xs italic text-zinc-600">
            No mentions this week.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {person.feedback_mentions.map((f) => (
              <li
                key={f.id}
                data-testid={`recap-feedback-${f.id}`}
                className="text-sm bg-indigo-500/5 border-l-2 border-indigo-500/60 rounded-r px-3 py-2"
              >
                <div className="text-xs text-zinc-500 font-mono mb-1">
                  {f.dispensary_name} · {fmt(f.tasting_date || f.submitted_at)}
                  {f.rating ? ` · rated ${f.rating}` : ""}
                </div>
                {f.shoutouts && (
                  <div className="text-zinc-200">
                    <span className="text-[10px] uppercase text-emerald-400 font-mono mr-1.5">
                      Shoutout:
                    </span>
                    {f.shoutouts}
                  </div>
                )}
                {f.feedback && (
                  <div className="text-zinc-300 mt-1">
                    <span className="text-[10px] uppercase text-zinc-500 font-mono mr-1.5">
                      Feedback:
                    </span>
                    {f.feedback}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// -- Sales Scorecard sheet ---------------------------------------------------
// Admin deep-dive on how every sales rep is working their book. Lives on
// its own `recap-sheet` so the Print-all flow emits it as a separate
// paginated page (looks like a leaderboard appended to the weekly recap).

function relDays(iso) {
  if (!iso) return { label: "never", days: Infinity, tone: "text-rose-300" };
  const now = new Date();
  const then = new Date(iso);
  const days = Math.round((now - then) / (1000 * 60 * 60 * 24));
  if (days < 1)  return { label: "today",   days, tone: "text-emerald-300" };
  if (days === 1) return { label: "1 day",  days, tone: "text-emerald-300" };
  if (days < 7)  return { label: `${days} days`, days, tone: "text-emerald-300" };
  if (days < 14) return { label: `${days} days`, days, tone: "text-amber-300" };
  if (days < 30) return { label: `${days} days`, days, tone: "text-amber-400" };
  return { label: `${days} days`, days, tone: "text-rose-300" };
}

function ScorecardSheet({ weekStart, weekEnd }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetchSalesScorecard().then(setData).catch((e) => setErr(e.message || "error"));
  }, []);

  const reps = data?.reps || [];
  const totals = useMemo(() => {
    const t = reps.reduce(
      (a, r) => ({
        assigned: a.assigned + r.assigned_count,
        touched: a.touched + r.touched_count,
        stale: a.stale + r.stale_count,
      }),
      { assigned: 0, touched: 0, stale: 0 }
    );
    t.pct = t.assigned ? Math.round((t.touched / t.assigned) * 100) : 0;
    return t;
  }, [reps]);

  return (
    <section
      data-testid="recap-sheet-scorecard"
      id="scorecard"
      className="recap-sheet panel p-6 flex flex-col gap-5"
    >
      <header className="flex items-start justify-between gap-4 border-b border-zinc-800 pb-4">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2.5">
            <TrendingUp className="w-5 h-5 text-emerald-300" />
            <h2 className="text-xl font-semibold tracking-tight text-zinc-50">
              Sales rep scorecard
            </h2>
          </div>
          <p className="text-xs text-zinc-500 font-mono">
            How every rep is working their accounts · Last 30 days · as of {fmt(new Date())}
          </p>
        </div>
        {reps.length > 0 && (
          <div className="hidden sm:flex items-center gap-6 text-right no-print">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Accounts</div>
              <div className="text-2xl font-semibold text-zinc-50">{totals.assigned}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Touched</div>
              <div className={`text-2xl font-semibold ${totals.pct >= 50 ? "text-emerald-400" : "text-amber-400"}`}>
                {totals.pct}%
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">Stale 30d</div>
              <div className={`text-2xl font-semibold ${totals.stale > 0 ? "text-rose-400" : "text-emerald-400"}`}>
                {totals.stale}
              </div>
            </div>
          </div>
        )}
      </header>

      {err ? (
        <div className="text-sm text-rose-300">Couldn't load scorecard — {err}</div>
      ) : !data ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-14 rounded-md bg-zinc-900/50" />
          ))}
        </div>
      ) : reps.length === 0 ? (
        <p className="text-xs italic text-zinc-600">No sales reps in Contacts yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table
            data-testid="scorecard-table"
            className="w-full text-sm border-separate border-spacing-0"
          >
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
                <th className="text-left py-2 px-3 border-b border-zinc-800 font-mono">Rep</th>
                <th className="text-right py-2 px-3 border-b border-zinc-800 font-mono">Accounts</th>
                <th className="text-right py-2 px-3 border-b border-zinc-800 font-mono">Touched</th>
                <th className="text-right py-2 px-3 border-b border-zinc-800 font-mono">Untouched</th>
                <th className="text-right py-2 px-3 border-b border-zinc-800 font-mono">Stale 30d</th>
                <th className="text-right py-2 px-3 border-b border-zinc-800 font-mono">Last touch</th>
              </tr>
            </thead>
            <tbody>
              {reps.map((rep) => {
                const pct = rep.assigned_count
                  ? Math.round((rep.touched_count / rep.assigned_count) * 100)
                  : 0;
                const last = relDays(rep.last_touch_at);
                const barTone =
                  pct >= 80 ? "bg-emerald-500"
                  : pct >= 50 ? "bg-amber-500"
                  : pct > 0 ? "bg-rose-500/80"
                  : "bg-zinc-700";
                const hasBook = rep.assigned_count > 0;
                return (
                  <tr
                    key={rep.contact_id}
                    id={`scorecard-${rep.contact_id}`}
                    data-testid={`scorecard-table-row-${rep.contact_id}`}
                    className="hover:bg-zinc-900/40 transition-colors"
                  >
                    <td className="py-3 px-3 border-b border-zinc-800/70">
                      <div className="flex flex-col gap-1">
                        <span className="text-zinc-100 font-medium">
                          {rep.name}
                        </span>
                        {hasBook && (
                          <div className="flex items-center gap-2 w-40">
                            <div className="flex-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                              <div
                                className={`h-full ${barTone}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-[10px] font-mono text-zinc-500 tabular-nums">
                              {pct}%
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-right text-zinc-200 font-mono border-b border-zinc-800/70 tabular-nums">
                      {rep.assigned_count}
                    </td>
                    <td className="py-3 px-3 text-right text-emerald-300 font-mono border-b border-zinc-800/70 tabular-nums">
                      {rep.touched_count || "—"}
                    </td>
                    <td className="py-3 px-3 text-right text-zinc-400 font-mono border-b border-zinc-800/70 tabular-nums">
                      {rep.untouched_count || "—"}
                    </td>
                    <td className="py-3 px-3 text-right font-mono border-b border-zinc-800/70 tabular-nums">
                      {rep.stale_count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-rose-300">
                          <AlertTriangle className="w-3 h-3" />
                          {rep.stale_count}
                        </span>
                      ) : hasBook ? (
                        <span className="text-emerald-400">0</span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                    <td className={`py-3 px-3 text-right font-mono border-b border-zinc-800/70 ${last.tone}`}>
                      {hasBook ? last.label : <span className="text-zinc-600">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-[10px] font-mono text-zinc-500 pt-1 flex items-center gap-1.5">
        <AlertCircle className="w-3 h-3" />
        A row is "stale" when no interaction has been logged on any of
        that rep's assigned shops in the last 30 days.
      </div>
    </section>
  );
}

// -- Page ---------------------------------------------------------------------
export default function Recap() {
  const [weekStart, setWeekStart] = useState(() => isoDate(toMonday(new Date())));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchRecap({ week_start: weekStart })
      .then((d) => active && setData(d))
      .catch((err) => {
        if (active) {
          toast.error("Could not load recap");
          // eslint-disable-next-line no-console
          console.error(err);
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [weekStart]);

  const shift = (days) => {
    const d = new Date(weekStart + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + days);
    setWeekStart(isoDate(toMonday(d)));
  };

  const goThisWeek = () => setWeekStart(isoDate(toMonday(new Date())));

  const teamSummary = useMemo(() => {
    if (!data?.team) return [];
    return data.team;
  }, [data]);

  return (
    <div data-testid="recap-page" className="flex flex-col gap-6">
      <style>{`
        /* ----------------------------------------------------------------
           Print-only: the Weekly Recap is designed as an editorial
           document, not a dump of coloured boxes. Content is left-aligned
           inside a comfortable reading column, metrics render as a flat
           receipt-style list, feedback becomes pull-quotes, and nothing
           is forced to stretch to fill the page. The on-screen UI is
           untouched — every rule below is scoped to body.recap-printing.
           ---------------------------------------------------------------- */
        @media print {
          @page { size: letter; margin: 0.75in 0.9in 0.75in 0.9in; }
          html, body { background: #fff !important; }
          .no-print, .hide-on-print { display: none !important; }
          body.recap-printing aside,
          body.recap-printing header[data-testid="app-header"] { display: none !important; }

          /* -- SHEET: standalone editorial document ---------------------- */
          body.recap-printing { background: #fff !important; }
          /* Admins don't need a printed handout — hide them from the
             print stream while keeping them visible on-screen for review */
          body.recap-printing .recap-sheet[data-role="admin"] { display: none !important; }
          /* Hide empty sections and zero-value metrics — the sheet should
             end where it ends, not pad with filler. Chained selectors so
             we outrank the display:flex rule on metric cards further down */
          body.recap-printing .recap-sheet .print-hidden { display: none !important; }
          body.recap-printing .recap-sheet [data-testid^="recap-metric-"][data-print-hide-zero=""] { display: none !important; }
          body.recap-printing .recap-sheet {
            background: #fff !important;
            color: #111 !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            padding: 0 !important;
            margin: 0 !important;
            page-break-after: always;
            break-after: page;
            font-family: 'IBM Plex Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif;
            font-size: 11pt;
            line-height: 1.5;
            max-width: 6.6in; /* breathable column instead of edge-to-edge */
          }
          body.recap-printing .recap-sheet:last-child {
            page-break-after: auto;
            break-after: auto;
          }

          /* All text defaults to graphite; accent rules override later */
          body.recap-printing .recap-sheet,
          body.recap-printing .recap-sheet p,
          body.recap-printing .recap-sheet li,
          body.recap-printing .recap-sheet td,
          body.recap-printing .recap-sheet th,
          body.recap-printing .recap-sheet span,
          body.recap-printing .recap-sheet div { color: #18181b !important; }
          body.recap-printing .recap-sheet .text-zinc-500,
          body.recap-printing .recap-sheet .text-zinc-600 { color: #71717a !important; }

          /* -- MASTHEAD: thin, understated. No giant green bar. ---------- */
          body.recap-printing .recap-sheet::before {
            content: "JackTrack  ·  Weekly Recap";
            display: block;
            font-family: 'IBM Plex Mono', ui-monospace, monospace;
            font-size: 8.5pt;
            letter-spacing: 0.28em;
            text-transform: uppercase;
            color: #065f46 !important;
            padding-bottom: 10px;
            border-bottom: 0.5pt solid #d4d4d8;
            margin-bottom: 36px;
          }

          /* -- FOOTER: just an attribution line -------------------------- */
          body.recap-printing .recap-sheet::after {
            content: "CalyFX  ·  Confidential";
            display: block;
            font-family: 'IBM Plex Mono', ui-monospace, monospace;
            font-size: 7.5pt;
            letter-spacing: 0.28em;
            text-transform: uppercase;
            color: #a1a1aa !important;
            padding-top: 14px;
            margin-top: 48px;
            border-top: 0.5pt solid #e4e4e7;
          }

          /* -- EMPLOYEE HEADER: name big, everything else subtitle ------- */
          body.recap-printing .recap-sheet > header {
            border-bottom: none !important;
            padding-bottom: 0 !important;
            margin-bottom: 32px !important;
            display: block !important;
          }
          body.recap-printing .recap-sheet > header h2 {
            font-size: 24pt !important;
            font-weight: 600 !important;
            letter-spacing: -0.01em !important;
            color: #09090b !important;
            line-height: 1.15 !important;
            margin: 0 !important;
          }
          body.recap-printing .recap-sheet > header p {
            font-family: 'IBM Plex Sans', sans-serif !important;
            font-size: 10pt !important;
            letter-spacing: normal !important;
            color: #52525b !important;
            margin-top: 4px !important;
          }
          /* The loud role chip becomes a subtle "— admin" tag */
          body.recap-printing .recap-sheet > header .bg-amber-500\\/15,
          body.recap-printing .recap-sheet > header .bg-sky-500\\/15,
          body.recap-printing .recap-sheet > header .bg-emerald-500\\/15,
          body.recap-printing .recap-sheet > header .bg-violet-500\\/15,
          body.recap-printing .recap-sheet > header .bg-cyan-500\\/15,
          body.recap-printing .recap-sheet > header .bg-orange-500\\/15,
          body.recap-printing .recap-sheet > header .bg-rose-500\\/15,
          body.recap-printing .recap-sheet > header [class*="bg-"][class*="border-"][class*="/15"],
          body.recap-printing .recap-sheet > header [class*="bg-"][class*="border-"][class*="/30"] {
            background: none !important;
            border: none !important;
            padding: 0 !important;
            margin-left: 10px !important;
            font-family: 'IBM Plex Sans', sans-serif !important;
            font-size: 11pt !important;
            font-weight: 400 !important;
            letter-spacing: normal !important;
            text-transform: none !important;
            color: #71717a !important;
            vertical-align: middle;
          }
          body.recap-printing .recap-sheet > header [class*="bg-"][class*="border-"][class*="/15"]::before,
          body.recap-printing .recap-sheet > header [class*="bg-"][class*="border-"][class*="/30"]::before {
            content: "— ";
            color: #a1a1aa !important;
          }

          /* -- SECTION HEADINGS: hairline underline, mono small-caps ----- */
          body.recap-printing .recap-sheet h3 {
            font-family: 'IBM Plex Mono', ui-monospace, monospace !important;
            font-size: 9pt !important;
            letter-spacing: 0.22em !important;
            text-transform: uppercase !important;
            color: #27272a !important;
            margin: 26px 0 12px 0 !important;
            padding-bottom: 6px !important;
            border-bottom: 0.5pt solid #e4e4e7 !important;
            display: block !important;
          }
          body.recap-printing .recap-sheet h3 svg { display: none !important; }

          /* -- METRICS: flat receipt-style rows (no cards, no grid) ----- */
          body.recap-printing .recap-sheet .grid { display: block !important; }
          body.recap-printing .recap-sheet [data-testid^="recap-metric-"] {
            display: flex !important;
            align-items: baseline !important;
            justify-content: space-between !important;
            flex-direction: row !important;
            background: none !important;
            border: none !important;
            border-bottom: 0.5pt dotted #e4e4e7 !important;
            border-radius: 0 !important;
            padding: 7px 0 !important;
            margin: 0 !important;
            page-break-inside: avoid;
            gap: 12px !important;
          }
          body.recap-printing .recap-sheet [data-testid^="recap-metric-"] > div:first-child {
            display: contents !important;
          }
          body.recap-printing .recap-sheet [data-testid^="recap-metric-"] > div:first-child > div:first-child {
            /* The metric LABEL */
            order: 1;
            font-family: 'IBM Plex Sans', sans-serif !important;
            font-size: 11pt !important;
            font-weight: 400 !important;
            letter-spacing: normal !important;
            text-transform: none !important;
            color: #27272a !important;
            flex: 1;
          }
          body.recap-printing .recap-sheet [data-testid^="recap-metric-"] > div:nth-child(2) {
            /* The number + delta chip wrapper */
            order: 2;
            display: inline-flex !important;
            align-items: baseline !important;
            gap: 10px !important;
            margin: 0 !important;
          }
          body.recap-printing .recap-sheet [data-testid^="recap-metric-"] .text-2xl {
            font-size: 16pt !important;
            font-weight: 600 !important;
            letter-spacing: -0.01em !important;
            color: #09090b !important;
            font-variant-numeric: tabular-nums;
          }
          /* Delta chip: tiny inline, colored arrow kept */
          body.recap-printing .recap-sheet [data-testid^="recap-metric-"] .inline-flex,
          body.recap-printing .recap-sheet .inline-flex.text-emerald-400,
          body.recap-printing .recap-sheet .inline-flex.text-rose-400 {
            background: none !important;
            border: none !important;
            padding: 0 !important;
            font-size: 9pt !important;
            font-family: 'IBM Plex Mono', monospace !important;
          }
          body.recap-printing .recap-sheet .text-emerald-400,
          body.recap-printing .recap-sheet .text-emerald-300 { color: #047857 !important; }
          body.recap-printing .recap-sheet .text-rose-400,
          body.recap-printing .recap-sheet .text-rose-300 { color: #b91c1c !important; }
          body.recap-printing .recap-sheet .text-amber-400,
          body.recap-printing .recap-sheet .text-amber-300 { color: #b45309 !important; }

          /* -- TASKS: indented bullets, admin-flagged ones with amber bar */
          body.recap-printing .recap-sheet ul { padding-left: 0 !important; margin: 0 !important; list-style: none !important; }
          body.recap-printing .recap-sheet [data-testid^="recap-task-"],
          body.recap-printing .recap-sheet [data-testid^="recap-admin-task-"] {
            background: none !important;
            border: none !important;
            border-radius: 0 !important;
            padding: 6px 0 6px 14px !important;
            margin: 0 0 4px 0 !important;
            border-left: 2pt solid #d4d4d8 !important;
            display: block !important;
            page-break-inside: avoid;
          }
          body.recap-printing .recap-sheet [data-testid^="recap-admin-task-"] {
            border-left-color: #b45309 !important;
            background: #fffbeb !important;
          }
          body.recap-printing .recap-sheet [data-testid^="recap-task-"] svg,
          body.recap-printing .recap-sheet [data-testid^="recap-admin-task-"] svg {
            display: none !important;
          }
          body.recap-printing .recap-sheet [data-testid^="recap-task-"] .font-medium,
          body.recap-printing .recap-sheet [data-testid^="recap-admin-task-"] .font-medium {
            font-weight: 500 !important;
          }
          /* "Assigned by admin" / "Other open tasks" sub-labels */
          body.recap-printing .recap-sheet .text-amber-400.uppercase,
          body.recap-printing .recap-sheet .uppercase.text-zinc-500 {
            font-family: 'IBM Plex Mono', monospace !important;
            font-size: 8pt !important;
            letter-spacing: 0.18em !important;
            color: #71717a !important;
            margin: 10px 0 6px 0 !important;
          }

          /* -- FEEDBACK: pull-quote treatment ---------------------------- */
          body.recap-printing .recap-sheet [data-testid^="recap-feedback-"] {
            background: none !important;
            border: none !important;
            border-left: 2pt solid #4338ca !important;
            border-radius: 0 !important;
            padding: 4px 0 6px 14px !important;
            margin: 0 0 14px 0 !important;
            page-break-inside: avoid;
          }
          body.recap-printing .recap-sheet [data-testid^="recap-feedback-"] > div:nth-child(1) {
            font-family: 'IBM Plex Mono', monospace !important;
            font-size: 8pt !important;
            letter-spacing: 0.08em !important;
            color: #71717a !important;
            margin-bottom: 4px !important;
          }
          body.recap-printing .recap-sheet [data-testid^="recap-feedback-"] > div:nth-child(2),
          body.recap-printing .recap-sheet [data-testid^="recap-feedback-"] > div:nth-child(3) {
            font-style: italic !important;
            color: #27272a !important;
          }

          /* -- SCORECARD SHEET: table-first, no colored fills ------------ */
          body.recap-printing [data-testid="recap-sheet-scorecard"] table {
            border-collapse: collapse !important;
            width: 100% !important;
          }
          body.recap-printing [data-testid="recap-sheet-scorecard"] table th {
            font-family: 'IBM Plex Mono', monospace !important;
            font-size: 8pt !important;
            letter-spacing: 0.15em !important;
            text-transform: uppercase !important;
            color: #71717a !important;
            border-bottom: 0.75pt solid #a1a1aa !important;
            padding: 8px 10px !important;
            text-align: right;
          }
          body.recap-printing [data-testid="recap-sheet-scorecard"] table th:first-child { text-align: left; }
          body.recap-printing [data-testid="recap-sheet-scorecard"] table td {
            border-bottom: 0.5pt solid #e4e4e7 !important;
            padding: 10px !important;
          }

          /* -- MISC colour cleanup (strip dark bgs) ---------------------- */
          body.recap-printing .recap-sheet .bg-zinc-900,
          body.recap-printing .recap-sheet .bg-zinc-900\\/40,
          body.recap-printing .recap-sheet .bg-zinc-900\\/60 { background: none !important; }
          body.recap-printing .recap-sheet .bg-zinc-800 { background: #e5e7eb !important; }
          body.recap-printing .recap-sheet .bg-emerald-500 { background: #047857 !important; }
          body.recap-printing .recap-sheet .bg-amber-500 { background: #b45309 !important; }
          body.recap-printing .recap-sheet .bg-rose-500\\/80 { background: #b91c1c !important; }
          body.recap-printing .recap-sheet .border-zinc-800,
          body.recap-printing .recap-sheet .border-zinc-800\\/70 { border-color: #e4e4e7 !important; }
          body.recap-printing .recap-sheet .italic.text-zinc-600 { color: #a1a1aa !important; }

          /* Keep orphans/widows tight so sections don't awkwardly split */
          body.recap-printing .recap-sheet p,
          body.recap-printing .recap-sheet li { orphans: 3; widows: 3; }
        }
      `}</style>

      {/* Toolbar */}
      <div
        data-testid="recap-toolbar"
        className="no-print flex flex-wrap items-center justify-between gap-3 panel px-4 py-3"
      >
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid="recap-prev-week"
            onClick={() => shift(-7)}
            className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-sm font-medium text-zinc-200 px-2 min-w-[180px] text-center">
            {data ? (
              <>
                {fmt(data.week_start)} – {fmt(data.week_end)}
              </>
            ) : (
              "—"
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            data-testid="recap-next-week"
            onClick={() => shift(7)}
            className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="recap-this-week"
            onClick={goThisWeek}
            className="bg-zinc-900 border-zinc-800 hover:bg-zinc-800 ml-1"
          >
            This week
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
            Mon–Sun · Monday meeting view
          </span>
          <Button
            data-testid="recap-print-all"
            onClick={() => {
              document.body.classList.add("recap-printing");
              window.print();
              setTimeout(() => document.body.classList.remove("recap-printing"), 500);
            }}
            className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25"
            size="sm"
          >
            <Printer className="w-3.5 h-3.5 mr-1.5" />
            Print all sheets
          </Button>
        </div>
      </div>

      {/* Team summary grid */}
      <div className="no-print panel p-5 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <UsersIcon className="w-4 h-4 text-zinc-500" />
          <h2 className="text-sm font-medium tracking-tight text-zinc-200">
            Team snapshot
          </h2>
          {loading && (
            <span className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider">
              loading…
            </span>
          )}
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        ) : teamSummary.length === 0 ? (
          <p className="text-xs italic text-zinc-600">No team members yet.</p>
        ) : (
          <div
            data-testid="recap-team-grid"
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
          >
            {teamSummary.map((p) => {
              const headlineKeys = HEADLINE_KEYS[p.role] || Object.keys(p.metrics || {});
              const total = headlineKeys.reduce(
                (a, k) => a + (p.metrics?.[k] || 0), 0,
              );
              return (
                <a
                  key={p.user_id}
                  href={`#recap-${p.user_id}`}
                  data-testid={`recap-summary-${p.user_id}`}
                  className="bg-zinc-900/60 hover:bg-zinc-900 border border-zinc-800 hover:border-zinc-700 rounded-md p-3 flex flex-col gap-1.5 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-zinc-100 truncate">
                      {p.name}
                    </span>
                    <span
                      className={`text-[9px] uppercase font-mono px-1.5 py-0.5 rounded border ${ROLE_COLOR[p.role] || "bg-zinc-800 border-zinc-700 text-zinc-300"}`}
                    >
                      {ROLE_LABEL[p.role] || p.role}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-semibold tracking-tight text-zinc-50">
                      {total}
                    </span>
                    <span className="text-[10px] uppercase text-zinc-500 font-mono">
                      total this week
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] font-mono text-zinc-500">
                    {(p.feedback_mentions?.length || 0) > 0 && (
                      <span className="text-indigo-400">
                        {p.feedback_mentions.length} mention
                        {p.feedback_mentions.length === 1 ? "" : "s"}
                      </span>
                    )}
                    {(p.open_tasks?.length || 0) > 0 && (
                      <span className="text-amber-400">
                        {p.open_tasks.length} open task
                        {p.open_tasks.length === 1 ? "" : "s"}
                      </span>
                    )}
                    {(p.admin_assigned_tasks?.length || 0) > 0 && (
                      <span className="inline-flex items-center gap-1 text-amber-300">
                        <CheckCircle2 className="w-3 h-3" />
                        admin task
                      </span>
                    )}
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>

      {/* Per-person printable sheets */}
      <div className="flex flex-col gap-4">
        {/* Scorecard sheet — admin dashboard-grade deep dive, included in Print all */}
        <ScorecardSheet
          weekStart={data?.week_start}
          weekEnd={data?.week_end}
        />
        {teamSummary.map((p) => (
          <div key={p.user_id} id={`recap-${p.user_id}`}>
            <PersonSheet
              person={p}
              weekStart={data?.week_start}
              weekEnd={data?.week_end}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
